import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { SidePanel, Tooltip } from "./Components";
import { Button } from "../../../Components/Button";
import useComponentVisible from "../../../hooks/useComponentVisible";
import { noop, isValidHttpUrl, withHttp, getRandomId } from "./utils";
import logger from "../../../utils/LoggerFrontend";
import * as sc from "./ChannelDataEditor/styles";
import { useTranslation } from "react-i18next";

function endsWithDomain(str) {
  const pattern = new RegExp("(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}", "i");
  return !!pattern.test(str);
}
const ChannelDataEditor = ({
  updateChannelData,
  value,
  dataKey,
  textStyle,
  label,
  limit,
  ellipsis,
  urlValue,
  bannedCharaters = [],
  editable,
  withLabel,
  padding,
  tooltip,
  dark,
  alias,
  Icon,
  loading
}) => {
  const { t } = useTranslation();

  logger.debug("ChannelDataEditor:constructor", "called", {});
  const inputRef = useRef();
  const [isChangedAnimation, setIsChangedAnimation] = useState(false);
  const [inputText, setInputText] = useState("");

  // change input value if prop value changes
  useEffect(() => {
    setInputText(value);
  }, [value]);

  // Reset input on close
  const closeEditingCallback = useCallback(() => {
    setInputText(value);
  }, [value]);

  const {
    ref: editorRef,
    isComponentVisible: editing,
    setIsComponentVisible: setEditing
  } = useComponentVisible(false, closeEditingCallback);

  const updateInputText = useCallback(
    (event) => {
      setInputText(dataKey === "url" ? withHttp(event.target.value) : event.target.value);
    },
    [dataKey]
  );

  const stopIsChangedAnimation = useCallback(() => setIsChangedAnimation(false), []);

  const openEditor = useCallback(() => {
    setEditing(true);
    inputRef.current.select();
  }, [setEditing]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    closeEditingCallback();
  }, [setEditing, closeEditingCallback]);

  const saveEditing = useCallback(() => {
    setIsChangedAnimation(true);
    updateChannelData({ key: dataKey, value: inputText });
    setEditing(false);
  }, [updateChannelData, dataKey, inputText, setEditing]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        saveEditing();
      }
    },
    [saveEditing]
  );

  // Validator
  const { valid, error } = useMemo(() => {
    if (!inputText) return { valid: true };

    if (dataKey === "displayLink") {
      if (!endsWithDomain(inputText)) {
        return { valid: false, error: t("Text must end with a domain name!") };
      }
    }
    if (dataKey === "url") {
      if (!isValidHttpUrl(inputText)) {
        return { valid: false, error: t("Text must be a valid URL!") };
      }
    }
    if (bannedCharaters?.length) {
      const characterErrors = bannedCharaters.filter((char) => inputText.includes(char));
      if (characterErrors?.length) {
        return {
          valid: false,
          error: t("Invalid characters: {{chars}}", { chars: characterErrors.join("") })
        };
      }
    }
    return { valid: true };
  }, [bannedCharaters, inputText, dataKey, t]);

  const tooltipId = useMemo(() => getRandomId(), []);

  return (
    <sc.ChannelDataEditorWrapper isChangedAnimation={isChangedAnimation} dark={dark}>
      <sc.ChannelDataWrapper
        ellipsis={ellipsis}
        onAnimationEnd={stopIsChangedAnimation}
        style={textStyle}
        editable={editable}
        onClick={editable ? openEditor : noop}
        dark={dark}
        padding={padding}
        data-tip=""
        data-for={tooltipId}
        data-target={`channel-data-${dataKey}`}
      >
        {withLabel && <div style={{ marginBottom: "4px" }}>{`${label}: `}</div>}
        {!editable && urlValue ? (
          <sc.ChannelDataLink target="_blank" href={urlValue}>
            {value}
          </sc.ChannelDataLink>
        ) : (
          alias || value || label
        )}
        {editable && Icon && !loading && (
          <sc.IconWrapper>
            <Icon />
          </sc.IconWrapper>
        )}
        {loading && (
          <sc.LoaderWrapper>
            <sc.Loader />
          </sc.LoaderWrapper>
        )}
      </sc.ChannelDataWrapper>

      {tooltip && editable && (
        <Tooltip
          id={tooltipId}
          effect="solid"
          place="top"
          backgroundColor="#1C1928"
          offset={{ top: -5 }}
        >
          {tooltip}
        </Tooltip>
      )}

      <SidePanel active={editing} alwaysVisible side="left" height="auto" offset={6}>
        <sc.Editor ref={editorRef}>
          <sc.EditorInfo>
            <sc.ChannelDataLabel htmlFor="channelData">{label}</sc.ChannelDataLabel>
            {!!limit && <sc.InputLimit>{`${inputText?.length || 0}/${limit}`}</sc.InputLimit>}
          </sc.EditorInfo>

          <sc.ChannelDataInputWrapper>
            <sc.ChannelDataInput
              name="channelData"
              type="text"
              value={inputText}
              onChange={updateInputText}
              onKeyDown={handleKeyDown}
              ref={inputRef}
              {...(limit ? { maxLength: limit } : {})}
            />

            <sc.EditorActions>
              <sc.CheckedButtonWrapper>
                <Button
                  variant="secondary"
                  onClick={saveEditing}
                  size="small"
                  disabled={!valid}
                  icon={<sc.Checked />}
                />
              </sc.CheckedButtonWrapper>

              <Button variant="tertiary" onClick={cancelEditing} size="small" icon={<sc.Clear />} />
            </sc.EditorActions>
          </sc.ChannelDataInputWrapper>

          <sc.ChannelDataError active={!!error} className="preview-error">
            {error}
          </sc.ChannelDataError>
        </sc.Editor>
      </SidePanel>
    </sc.ChannelDataEditorWrapper>
  );
};

export default ChannelDataEditor;
